import React, { useRef, useState } from "react";
import { Ripple } from "primereact/ripple";
import { Dialog } from 'primereact/dialog';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { printPlugin } from '@react-pdf-viewer/print';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/print/lib/styles/index.css';

import packageJson from '../../../package.json';
import { Toast } from "primereact/toast";

interface DocumentViewModalProps {
    isOpen: boolean;
    onClose: () => void;
    onDelete?: () => void;
    onEdit?: () => void;
    fileUrl: string;
    hasDelete?: boolean;
    hasEdit?: boolean;
    canDownload?: boolean;
}

const DocumentViewModal: React.FC<DocumentViewModalProps> = ({ isOpen, onClose, onDelete, onEdit, fileUrl, hasDelete, hasEdit, canDownload }) => {
    const toast = useRef<Toast>(null);
    const defaultLayoutPluginInstance = defaultLayoutPlugin();
    const printPluginInstance = printPlugin();
    const pdfjsVersion = packageJson.dependencies['pdfjs-dist'].replace('^', '');
    const [isDownloading, setIsDownloading] = useState<boolean>(false);


    const handleDownloadFile = async () => {
        if (isDownloading) return;
        const filePath = fileUrl;

        if (!filePath) {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "File not found!",
                    life: 3000,
                });
            }
            return;
        }

        setIsDownloading(true);

        setTimeout(async () => {
            try {
                const response = await fetch(filePath);

                if (!response.ok) {
                    throw new Error("Failed to fetch the file.");
                }

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = filePath.split("/").pop() || 'file';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "File downloaded successfully.",
                        life: 3000,
                    });
                }
            } catch (error) {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "Error downloading the file",
                        life: 3000,
                    });
                }
            } finally {
                setIsDownloading(false);
            }
        }, 500);
    };

    const headerTemplate = (
        <div className={`document_modal_header_inner ${hasDelete || hasEdit || canDownload ? 'justify-content-between' : 'justify-content-end'}`}>
            {hasDelete && (
                <button type="button" className="close_modal_btn d-flex align-items-center gap-2 danger p-ripple"
                    onClick={onDelete}>
                    <i className="bi bi-trash3"></i>
                    <span>Delete File</span>
                    <Ripple />
                </button>
            )}

            {hasEdit && (
                <button type="button" className="close_modal_btn primary d-flex align-items-center gap-2 p-ripple"
                    onClick={onEdit}>
                    <i className="bi bi-pencil-square"></i>
                    <span>Change File</span>
                    <Ripple />
                </button>
            )}

            {canDownload && (
                <button type="button" className="close_modal_btn primary d-flex align-items-center gap-2 p-ripple"
                    onClick={handleDownloadFile}>
                    <i className={`bi ${isDownloading ? 'bi bi-arrow-repeat loading_spinner' : ' bi-download'}`}></i>
                    <span>{isDownloading ? 'Downloading' : 'Download File'}</span>
                    <Ripple />
                </button>
            )}
        </div>
    )

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <Dialog
                visible={isOpen}
                headerClassName="document_modal_header"
                header={headerTemplate}
                maximizable={fileUrl ? true : false}
                className="custom_modal_dialog modal_dialog_xl"
                onHide={onClose}
                contentClassName="pt-0"
                dismissableMask
                maximized={true}
            >
                <div className="custom_modal_body h-100 pt-0">
                    {fileUrl ? (
                        <Worker workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`}>
                            <Viewer fileUrl={fileUrl} plugins={[defaultLayoutPluginInstance, printPluginInstance]} />
                        </Worker>
                    ) : (
                        <div className="no_document_available_area">
                            <img src="/assets/images/no_data/no_document.png" alt="No document available." />
                            <p>No document available.</p>
                        </div>
                    )}
                </div>
            </Dialog>
        </React.Fragment>
    )
}

export default DocumentViewModal;